import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, ClipboardList, Edit2, Layers, ArrowUp, ArrowDown, CheckCircle2, MinusCircle, Circle, X, MessageSquare, Upload, Clock, Download, AlertTriangle, Mail, Send,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { v: "planning", label: "Planning", color: "bg-slate-100 text-slate-700" },
  { v: "in_progress", label: "In progress", color: "bg-blue-100 text-blue-800" },
  { v: "on_hold", label: "On hold", color: "bg-amber-100 text-amber-800" },
  { v: "completed", label: "Completed", color: "bg-emerald-100 text-emerald-800" },
  { v: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-800" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.v, s]));

const STEP_STATUSES = [
  { v: "open", label: "Open", color: "bg-white text-slate-700 border-slate-200", icon: Circle, dotColor: "text-slate-300" },
  { v: "completed", label: "Completed", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, dotColor: "text-emerald-600" },
  { v: "not_required", label: "Not required", color: "bg-slate-50 text-slate-500 border-slate-200", icon: MinusCircle, dotColor: "text-slate-400" },
];
const STEP_STATUS_MAP = Object.fromEntries(STEP_STATUSES.map((s) => [s.v, s]));

const fmtMoney = (n) => {
  if (n == null) return "—";
  const num = Number(n) || 0;
  return `£${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtMinutes = (m) => {
  const n = Number(m) || 0;
  if (!n) return "0m";
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60);
  const mm = n % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
};

// Days between two ISO/date strings — used for the "Age" column on the projects list.
// Returns null when either date is missing so the UI can render an em-dash.
const daysBetween = (from, to = null) => {
  if (!from) return null;
  const start = new Date(from);
  if (Number.isNaN(start.getTime())) return null;
  const end = to ? new Date(to) : new Date();
  if (Number.isNaN(end.getTime())) return null;
  const ms = end.setHours(0, 0, 0, 0) - start.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round(ms / 86400000));
};

// ---- RAG (Red / Amber / Green) status ----
// Defaults mirror the backend so the UI never has to wait for a network round-trip
// before rendering the first frame.
const DEFAULT_RAG_CONFIG = {
  age_amber_days: 30,
  age_red_days: 60,
  inactivity_amber_days: 7,
  inactivity_red_days: 14,
  enabled: true,
};

// Find the most recent step-level completed_at on a project. Null when there's
// been no task completion yet.
function lastTaskCompletedAt(project) {
  if (!project?.steps?.length) return null;
  let latest = null;
  for (const s of project.steps) {
    if ((s.status === "completed" || s.status === "not_required") && s.completed_at) {
      if (!latest || s.completed_at > latest) latest = s.completed_at;
    }
  }
  return latest;
}

// Compute the RAG bucket for a project. Returns "neutral" when the project is
// closed (completed / cancelled) or when RAG is disabled globally.
// Colour is the WORSE of the two metrics — age of project vs days since last
// completed task.
function computeProjectRag(project, cfg) {
  if (!cfg?.enabled) return { level: "neutral", reason: "RAG disabled" };
  if (!project) return { level: "neutral", reason: "" };
  if (project.status === "completed" || project.status === "cancelled") {
    return { level: "neutral", reason: `Project ${project.status}` };
  }
  const ageDays = daysBetween(project.start_date || project.created_at);
  const lastDone = lastTaskCompletedAt(project);
  // If no task has been completed yet, fall back to project age for inactivity too.
  const inactivityDays = daysBetween(lastDone || project.start_date || project.created_at);

  const bucket = (d, amber, red) => {
    if (d == null) return { level: "neutral", days: null };
    if (d > red) return { level: "red", days: d };
    if (d > amber) return { level: "amber", days: d };
    return { level: "green", days: d };
  };
  const ageB = bucket(ageDays, cfg.age_amber_days, cfg.age_red_days);
  const inaB = bucket(inactivityDays, cfg.inactivity_amber_days, cfg.inactivity_red_days);
  const order = { neutral: 0, green: 1, amber: 2, red: 3 };
  const worst = order[ageB.level] >= order[inaB.level] ? ageB : inaB;
  const level = order[ageB.level] === order[inaB.level] && ageB.level === "green" ? "green" : worst.level;
  return {
    level,
    age_days: ageDays,
    inactivity_days: inactivityDays,
    last_task_at: lastDone,
    reason:
      level === "red"
        ? worst === ageB
          ? `Project is ${ageDays}d old (> ${cfg.age_red_days}d)`
          : `No task completed for ${inactivityDays}d (> ${cfg.inactivity_red_days}d)`
        : level === "amber"
        ? worst === ageB
          ? `Project is ${ageDays}d old (> ${cfg.age_amber_days}d)`
          : `No task completed for ${inactivityDays}d (> ${cfg.inactivity_amber_days}d)`
        : "On track",
  };
}

const RAG_STYLES = {
  red:     { row: "bg-red-50/70 hover:bg-red-50",      dot: "bg-red-500",      label: "text-red-700" },
  amber:   { row: "bg-amber-50/60 hover:bg-amber-50",  dot: "bg-amber-500",    label: "text-amber-700" },
  green:   { row: "bg-emerald-50/40 hover:bg-emerald-50", dot: "bg-emerald-500", label: "text-emerald-700" },
  neutral: { row: "hover:bg-slate-50",                 dot: "bg-slate-300",    label: "text-slate-500" },
};

// Trigger a client-side download of a small CSV template so the user knows the
// exact column layout expected by /api/project-templates/parse-csv.
const CSV_TEMPLATE_CONTENT = [
  "name,description,estimated_minutes,group",
  "Kick-off call with customer IT lead,60-min discovery — scope + blockers,60,Discovery",
  "Provision tenant,Create tenant and apply base policy templates,45,Discovery",
  "Wave 1 workstation rollout,Deploy agents to first 80 endpoints,180,Falcon EDR",
  "Wave 2 workstation rollout,Deploy agents to remaining 170 endpoints,240,Falcon EDR",
  "Tune detection policies,Review noisy detections + suppress false positives,90,Falcon EDR",
  "Customer runbook walkthrough,Handover documentation and Q&A,60,Handover",
  "30-day post-deployment review,Confirm coverage + tuning is stable,45,Handover",
].join("\n") + "\n";

function downloadCsvTemplate() {
  const blob = new Blob([CSV_TEMPLATE_CONTENT], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "xentra_project_template_tasks.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function Projects() {
  const [tab, setTab] = useState("projects");
  return (
    <div className="space-y-6" data-testid="projects-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Onboarding</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Projects</h1>
        <p className="text-slate-500 text-sm mt-1">
          Track installs and onboarding before accounts move into the support phase.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 gap-1">
          <TabsTrigger value="projects" data-testid="projects-tab-projects"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:shadow-none px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-900">
            Active projects
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="projects-tab-templates"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:shadow-none px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-900">
            Templates
          </TabsTrigger>
          <TabsTrigger value="config" data-testid="projects-tab-config"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:shadow-none px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-900">
            Configuration
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-6">
          <ProjectsList />
        </TabsContent>
        <TabsContent value="templates" className="mt-6">
          <TemplatesList />
        </TabsContent>
        <TabsContent value="config" className="mt-6">
          <ProjectsConfig />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Projects list (table view) ---------------- */
function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [ragCfg, setRagCfg] = useState(DEFAULT_RAG_CONFIG);
  // Default to "All open" = planning + in_progress + on_hold (excludes completed/cancelled)
  const [statusFlt, setStatusFlt] = useState("open_all");
  const [sortBy, setSortBy] = useState("customer_asc");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    // Fetch everything and let the client filter — we need to switch between
    // multi-status "open_all" and single-status filters without extra round-trips.
    const [p, a, t, s, cfg] = await Promise.all([
      api.get("/projects"),
      api.get("/accounts"),
      api.get("/project-templates"),
      api.get("/staff").catch(() => ({ data: [] })),
      api.get("/projects/rag-config").catch(() => ({ data: DEFAULT_RAG_CONFIG })),
    ]);
    setProjects(p.data);
    setAccounts(a.data);
    setTemplates(t.data);
    setStaff(s.data);
    setRagCfg({ ...DEFAULT_RAG_CONFIG, ...(cfg.data || {}) });
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let list = projects.filter((p) => {
      if (statusFlt === "open_all") {
        if (!["planning", "in_progress", "on_hold"].includes(p.status)) return false;
      } else if (statusFlt !== "all" && p.status !== statusFlt) {
        return false;
      }
      if (ql) {
        const hay = `${p.name || ""} ${p.account_name || ""} ${p.owner_name || ""} ${p.csm_name || ""}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
    // Sort
    const key = sortBy;
    const bySafe = (s) => (s || "").toString().toLowerCase();
    const byDate = (s) => (s ? new Date(s).getTime() : 0);
    list = [...list].sort((a, b) => {
      switch (key) {
        case "customer_asc":  return bySafe(a.account_name).localeCompare(bySafe(b.account_name));
        case "customer_desc": return bySafe(b.account_name).localeCompare(bySafe(a.account_name));
        case "start_asc":     return byDate(a.start_date) - byDate(b.start_date);
        case "start_desc":    return byDate(b.start_date) - byDate(a.start_date);
        case "recent":        return byDate(b.created_at) - byDate(a.created_at);
        default:              return 0;
      }
    });
    return list;
  }, [projects, q, statusFlt, sortBy]);

  if (active) {
    return (
      <ProjectDetail
        projectId={active}
        staff={staff}
        onBack={() => { setActive(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search project, customer, owner, CSM…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-72 h-9"
            data-testid="project-search"
          />
          <Select value={statusFlt} onValueChange={setStatusFlt}>
            <SelectTrigger className="w-44 h-9 text-xs" data-testid="filter-project-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open_all">All open (planning + in progress + on hold)</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 h-9 text-xs" data-testid="filter-project-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="customer_asc">Customer A → Z</SelectItem>
              <SelectItem value="customer_desc">Customer Z → A</SelectItem>
              <SelectItem value="start_asc">Start date · earliest first</SelectItem>
              <SelectItem value="start_desc">Start date · latest first</SelectItem>
              <SelectItem value="recent">Recently created</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500 font-mono">{rows.length} shown</span>
        </div>
        <div className="flex items-center gap-2">
          <SendProjectUpdateDialog rows={rows} ragCfg={ragCfg} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn" className="gap-2 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> New Project</Button>
            </DialogTrigger>
            <NewProjectDialog
              accounts={accounts}
              templates={templates}
              staff={staff}
              onClose={() => setOpen(false)}
              onCreated={(p) => { setOpen(false); load(); setActive(p.id); }}
            />
          </Dialog>
        </div>
      </div>

      {rows.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">No projects match</div>
          <div className="text-sm text-slate-500 mt-1">
            Try adjusting the filter or clear your search.
          </div>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="grid grid-cols-[24px_minmax(220px,2.2fr)_130px_130px_130px_100px_80px_90px_110px] gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 sticky top-0 z-10">
            <span title="RAG status"></span>
            <span>Customer / Project</span>
            <span>Progress</span>
            <span>Owner</span>
            <span>CSM</span>
            <span>Start</span>
            <span>Age</span>
            <span className="text-right pr-2">MRR</span>
            <span>Status</span>
          </div>
          <div className="divide-y divide-slate-100">
            {rows.map((p) => {
              const st = STATUS_MAP[p.status] || STATUS_MAP.planning;
              const rag = computeProjectRag(p, ragCfg);
              const rs = RAG_STYLES[rag.level] || RAG_STYLES.neutral;
              const ragTitle = rag.reason
                ? `${rag.level.toUpperCase()} · ${rag.reason}`
                : rag.level.toUpperCase();
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setActive(p.id)}
                  data-testid={`project-row-${p.id}`}
                  data-rag={rag.level}
                  className={`w-full grid grid-cols-[24px_minmax(220px,2.2fr)_130px_130px_130px_100px_80px_90px_110px] gap-3 px-4 py-3 text-left text-sm items-center transition-colors ${rs.row}`}
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full inline-block ${rs.dot}`}
                    title={ragTitle}
                    data-testid={`project-rag-dot-${p.id}`}
                  />
                  <div className="min-w-0">
                    <div
                      onClick={(e) => { e.stopPropagation(); nav(`/app/accounts/${p.account_id}`); }}
                      className="text-sm font-semibold text-blue-700 hover:text-blue-900 hover:underline truncate"
                      data-testid={`project-row-customer-${p.id}`}
                    >
                      {p.account_name}
                    </div>
                    <div className="text-sm text-slate-900 truncate mt-0.5">{p.name}</div>
                    {p.template_name && (
                      <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider mt-0.5">
                        From template · {p.template_name}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between items-center text-[11px] mb-1">
                      <span className="font-mono text-slate-500">{p.steps_done}/{p.steps_total}</span>
                      <span className="font-mono font-semibold text-slate-700">{p.progress_pct}%</span>
                    </div>
                    <Progress value={p.progress_pct} className="h-1.5" />
                  </div>
                  <span className="truncate text-slate-700 text-xs">{p.owner_name || <span className="text-slate-400">—</span>}</span>
                  <span className="truncate text-slate-700 text-xs">{p.csm_name || <span className="text-slate-400">—</span>}</span>
                  <span className="font-mono text-[11px] text-slate-600">{p.start_date ? fmtDate(p.start_date) : "—"}</span>
                  <ProjectAgeCell project={p} />
                  <span className="font-mono text-[11px] text-slate-700 text-right pr-2">{fmtMoney(p.mrr)}</span>
                  <span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${st.color}`}>
                      {st.label}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ---------------- Small "Age" cell for the project list ---------------- */
function ProjectAgeCell({ project }) {
  // Age is measured from the START DATE. Only cap at completion when the project
  // is CURRENTLY completed — a re-opened project keeps counting from its original
  // start date, ignoring any stale completed_at from a previous closure.
  const isDone = project.status === "completed";
  const from = project.start_date || project.created_at;
  const to = isDone ? (project.completed_at || null) : null;
  const days = daysBetween(from, to);
  if (days == null) {
    return <span className="font-mono text-[11px] text-slate-400">—</span>;
  }
  const label = days === 0 ? "today" : days === 1 ? "1 day" : `${days} days`;
  // Colour-code so long-running open projects catch the eye
  let cls = "text-slate-700";
  if (!isDone) {
    if (days >= 60) cls = "text-red-700";
    else if (days >= 30) cls = "text-amber-700";
  } else {
    cls = "text-slate-500";
  }
  return (
    <span
      className={`font-mono text-[11px] ${cls}`}
      data-testid={`project-row-age-${project.id}`}
      title={isDone ? `Closed after ${label}` : `Open for ${label}`}
    >
      {isDone ? `${label} · closed` : label}
    </span>
  );
}

/* ---------------- Send Project Update (weekly email) ---------------- */
const PROJECT_UPDATE_INTRO = [
  "Please see this week's projects update.",
  "If there's any information on here that's incorrect or you need to discuss, please reach out to technical ahead of next week's project catch up meeting.",
  "RAG is based on project age and inactivity: Amber = 30+ days old or 7+ days inactive; Green = 60+ days old or 14+ days inactive.",
];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function ageLabel(p) {
  const isDone = p.status === "completed";
  const to = isDone ? (p.completed_at || null) : null;
  const days = daysBetween(p.start_date || p.created_at, to);
  if (days == null) return "—";
  const label = days === 0 ? "today" : days === 1 ? "1 day" : `${days} days`;
  return isDone ? `${label} · closed` : label;
}

function SendProjectUpdateDialog({ rows, ragCfg }) {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState(["allhands@xentra.co.uk"]);
  const [entry, setEntry] = useState("");
  const [sending, setSending] = useState(false);

  const RAG_DOT = { red: "bg-red-500", amber: "bg-amber-500", green: "bg-emerald-500", neutral: "bg-slate-300" };

  const buildRows = () => rows.map((p) => {
    const rag = computeProjectRag(p, ragCfg);
    return {
      id: p.id,
      rag: rag.level,
      customer: p.account_name || "",
      project: p.name || "",
      template_name: p.template_name || null,
      progress: `${p.steps_done ?? 0}/${p.steps_total ?? 0}`,
      progress_pct: p.progress_pct ?? 0,
      owner: p.owner_name || "",
      csm: p.csm_name || "",
      start: p.start_date ? fmtDate(p.start_date) : "—",
      age: ageLabel(p),
      mrr: fmtMoney(p.mrr),
      status: (STATUS_MAP[p.status] || STATUS_MAP.planning).label,
      next_action: p.next_action || "",
    };
  });

  const addEntry = () => {
    const parts = entry.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const next = [...recipients];
    for (const e of parts) {
      if (!EMAIL_RE.test(e)) { toast.error(`Not a valid email: ${e}`); continue; }
      if (!next.some((x) => x.toLowerCase() === e.toLowerCase())) next.push(e);
    }
    setRecipients(next);
    setEntry("");
  };

  const removeRecipient = (e) => setRecipients(recipients.filter((x) => x !== e));

  const send = async () => {
    let recips = [...recipients];
    if (entry.trim()) {
      const e = entry.trim();
      if (EMAIL_RE.test(e) && !recips.some((x) => x.toLowerCase() === e.toLowerCase())) recips.push(e);
    }
    if (recips.length === 0) return toast.error("Add at least one recipient");
    if (rows.length === 0) return toast.error("There are no projects to include");
    setSending(true);
    try {
      const r = await api.post("/projects/send-update", { recipients: recips, rows: buildRows() });
      toast.success(`Projects update sent to ${r.data.sent_to.length} recipient${r.data.sent_to.length === 1 ? "" : "s"}`);
      setOpen(false);
      setEntry("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send the update");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 border-slate-300" data-testid="send-project-update-btn">
          <Mail className="h-4 w-4" /> Send Project Update
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="send-project-update-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4 text-blue-600" /> Send weekly projects update</DialogTitle>
          <DialogDescription>
            Emails the current Active Projects table ({rows.length} project{rows.length === 1 ? "" : "s"}) with RAG colours and a Next Action column. No export needed.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-600 space-y-1.5">
          {PROJECT_UPDATE_INTRO.map((line, i) => (
            <p key={i} className={i === 2 ? "text-slate-500" : ""}>{line}</p>
          ))}
        </div>

        <div>
          <Label className="text-xs">Recipients</Label>
          <div className="mt-1 flex flex-wrap gap-1.5 rounded-md border border-slate-200 p-2 min-h-[42px]" data-testid="recipient-chips">
            {recipients.map((e) => (
              <span key={e} className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 text-xs px-2 py-0.5 border border-blue-200">
                {e}
                <button type="button" onClick={() => removeRecipient(e)} data-testid={`remove-recipient-${e}`} className="hover:text-blue-900">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEntry(); } }}
              onBlur={() => { if (entry.trim()) addEntry(); }}
              placeholder="Add email + Enter"
              className="flex-1 min-w-[160px] text-sm outline-none bg-transparent"
              data-testid="recipient-input"
            />
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Defaults to allhands@xentra.co.uk. Add or remove addresses as needed.</p>
        </div>

        <div className="max-h-[240px] overflow-auto rounded-md border border-slate-200">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left w-8"></th>
                <th className="px-2 py-1.5 text-left">Customer / Project</th>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-left">Next Action</th>
              </tr>
            </thead>
            <tbody>
              {buildRows().map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1.5"><span className={`inline-block h-2.5 w-2.5 rounded-full ${RAG_DOT[r.rag] || RAG_DOT.neutral}`} /></td>
                  <td className="px-2 py-1.5"><span className="font-medium text-slate-800">{r.customer}</span> · {r.project}</td>
                  <td className="px-2 py-1.5 text-slate-600">{r.status}</td>
                  <td className="px-2 py-1.5 text-slate-600">{r.next_action || <span className="text-slate-400 italic">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="send-update-cancel">Cancel</Button>
          <Button onClick={send} disabled={sending} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="send-update-confirm">
            <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Project detail with checklist + reorder + multi-select ---------------- */
function ProjectDetail({ projectId, staff, onBack }) {
  const [project, setProject] = useState(null);
  const [headerEdit, setHeaderEdit] = useState(false);
  const [form, setForm] = useState(null);           // header fields
  const [reordering, setReordering] = useState(false);
  const [localSteps, setLocalSteps] = useState([]); // when reordering
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [completeDialog, setCompleteDialog] = useState(null); // { stepId, name, estMinutes }
  const [notesDialog, setNotesDialog] = useState(null);       // { stepId, name, current }
  // TaskEditor drives both "add a new task" and "edit an existing task". When
  // stepId is null it's an add flow, when set it's an edit.
  const [taskEditor, setTaskEditor] = useState(null);         // { mode:'add'|'edit', stepId?, initial:{...} }
  const [bulkStatus, setBulkStatus] = useState(null); // "completed" | "not_required" | "open"
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get(`/projects/${projectId}`);
    setProject(r.data);
    setForm({
      name: r.data.name || "",
      description: r.data.description || "",
      status: r.data.status || "planning",
      start_date: r.data.start_date || "",
      target_date: r.data.target_date || "",
      owner_name: r.data.owner_name || "",
      csm_name: r.data.csm_name || "",
      mrr: r.data.mrr != null ? String(r.data.mrr) : "",
      next_action: r.data.next_action || "",
      completed_date: r.data.completed_at ? r.data.completed_at.slice(0, 10) : "",
    });
    setLocalSteps(r.data.steps || []);
    setSelectedIds(new Set());
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  if (!project) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;
  const st = STATUS_MAP[project.status] || STATUS_MAP.planning;

  const saveHeader = async () => {
    setSaving(true);
    try {
      const mrrNum = form.mrr === "" ? null : Number(form.mrr);
      const payload = {
        name: form.name,
        description: form.description || null,
        status: form.status,
        start_date: form.start_date || null,
        target_date: form.target_date || null,
        owner_name: form.owner_name || null,
        csm_name: form.csm_name || null,
        mrr: Number.isFinite(mrrNum) ? mrrNum : null,
        next_action: form.next_action ? form.next_action : null,
      };
      if (form.status === "completed") {
        payload.completed_at = form.completed_date ? `${form.completed_date}T12:00:00+00:00` : null;
      }
      await api.patch(`/projects/${projectId}`, payload);
      toast.success("Project updated");
      setHeaderEdit(false);
      await load();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${projectId}`);
    toast.success("Project deleted");
    onBack();
  };

  // ---- Step actions ----
  const patchStep = async (stepId, body) => {
    const r = await api.patch(`/projects/${projectId}/steps/${stepId}`, body);
    setProject(r.data);
    setLocalSteps(r.data.steps || []);
    setSelectedIds((s) => { const n = new Set(s); n.delete(stepId); return n; });
  };

  const markOpen = (stepId) => patchStep(stepId, { status: "open" });
  const markNotRequired = (stepId) => patchStep(stepId, { status: "not_required" });
  const askComplete = (step) => setCompleteDialog({ stepId: step.id, name: step.name, estMinutes: step.estimated_minutes || 0 });
  const askEditNotes = (step) => setNotesDialog({ stepId: step.id, name: step.name, current: step.notes || "" });

  // Convert the date input (YYYY-MM-DD) into an ISO string. When the user
  // picks today, send null so the backend uses "now" (preserving the current
  // wall-clock time). For any other date, use midday UTC on that date.
  const dateToCompletedAt = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date().toISOString().slice(0, 10);
    if (dateStr === today) return null;
    return `${dateStr}T12:00:00+00:00`;
  };

  const confirmComplete = async ({ time, notes, completedDate }) => {
    if (!completeDialog) return;
    await patchStep(completeDialog.stepId, {
      status: "completed",
      time_spent_minutes: Number(time) || 0,
      notes: notes || null,
      completed_at: dateToCompletedAt(completedDate),
    });
    toast.success("Task marked complete");
    setCompleteDialog(null);
  };

  const confirmNotes = async (notes) => {
    if (!notesDialog) return;
    await patchStep(notesDialog.stepId, { notes: notes ? notes : null });
    toast.success(notes ? "Notes saved" : "Notes cleared");
    setNotesDialog(null);
  };

  // ---- Task add / edit / delete ----
  const openAddTask = () => setTaskEditor({
    mode: "add",
    stepId: null,
    initial: { name: "", description: "", group: "", estimated_minutes: "" },
  });
  const openEditTask = (step) => setTaskEditor({
    mode: "edit",
    stepId: step.id,
    initial: {
      name: step.name || "",
      description: step.description || "",
      group: step.group || "",
      estimated_minutes: step.estimated_minutes != null ? String(step.estimated_minutes) : "",
    },
  });
  const submitTaskEditor = async ({ name, description, group, estimated_minutes }) => {
    if (!taskEditor) return;
    const cleaned = {
      name: (name || "").trim(),
      description: description || null,
      group: (group && group.trim()) ? group.trim() : null,
      estimated_minutes: Number(estimated_minutes) || 0,
    };
    if (!cleaned.name) return toast.error("Task name is required");
    try {
      if (taskEditor.mode === "add") {
        const r = await api.post(`/projects/${projectId}/steps`, cleaned);
        setProject(r.data);
        setLocalSteps(r.data.steps || []);
        toast.success("Task added");
      } else {
        // Edit — patch just the changed set of fields
        const r = await api.patch(`/projects/${projectId}/steps/${taskEditor.stepId}`, cleaned);
        setProject(r.data);
        setLocalSteps(r.data.steps || []);
        toast.success("Task updated");
      }
      setTaskEditor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deleteTask = async (step) => {
    if (!window.confirm(`Delete task "${step.name}"? This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/projects/${projectId}/steps/${step.id}`);
      setProject(r.data);
      setLocalSteps(r.data.steps || []);
      setSelectedIds((s) => { const n = new Set(s); n.delete(step.id); return n; });
      toast.success("Task deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ---- Reorder ----
  const move = (idx, dir) => {
    const next = [...localSteps];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setLocalSteps(next);
  };
  const saveReorder = async () => {
    setSaving(true);
    try {
      const step_ids = localSteps.map((s) => s.id);
      const r = await api.post(`/projects/${projectId}/steps/reorder`, { step_ids });
      setProject(r.data);
      setLocalSteps(r.data.steps || []);
      setReordering(false);
      toast.success("Order saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };
  const cancelReorder = () => { setLocalSteps(project.steps || []); setReordering(false); };

  // ---- Bulk status ----
  const applyBulk = async (status, extra = {}) => {
    if (selectedIds.size === 0) return;
    try {
      const r = await api.post(`/projects/${projectId}/steps/bulk-status`, {
        step_ids: Array.from(selectedIds),
        status,
        ...extra,
      });
      setProject(r.data);
      setLocalSteps(r.data.steps || []);
      setSelectedIds(new Set());
      setBulkStatus(null);
      toast.success(`${selectedIds.size} task(s) updated`);
    } catch (e) {
      toast.error("Bulk update failed");
    }
  };

  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // Rename every task tagged with a given group name. Uses per-step PATCH so
  // the server-side change stays fully audited via the existing patch_step flow.
  const renameGroup = async (currentName) => {
    const next = window.prompt(`Rename group "${currentName}" to:`, currentName);
    if (next == null) return;
    const trimmed = next.trim();
    if (trimmed === currentName) return;
    const affected = localSteps.filter((s) => (s.group || "") === currentName);
    if (affected.length === 0) return;
    try {
      // Patch sequentially — group PATCHes are tiny and this keeps error-handling simple.
      for (const s of affected) {
        await api.patch(`/projects/${projectId}/steps/${s.id}`, { group: trimmed || null });
      }
      toast.success(`Renamed group (${affected.length} task${affected.length === 1 ? "" : "s"})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename group");
      await load();
    }
  };
  const selectAll = () => setSelectedIds(new Set(localSteps.map((s) => s.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const stepsToShow = localSteps;

  return (
    <div className="space-y-5" data-testid="project-detail">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <button onClick={onBack} data-testid="project-back" className="hover:text-slate-900 inline-flex items-center gap-1">← Back to projects</button>
        <span>/</span>
        <span className="text-slate-900 font-medium">{project.name}</span>
      </div>

      {/* Header card */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-slate-950 to-blue-950 text-white p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-[0.2em] font-mono text-blue-300">{project.account_name}</div>
              <h2 className="font-heading text-2xl font-bold mt-1">{project.name}</h2>
              {project.description && <div className="text-sm text-slate-300 mt-2 max-w-2xl">{project.description}</div>}
              <div className="mt-3 flex items-center gap-3 text-[11px] font-mono text-slate-300 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${st.color}`}>{st.label}</span>
                {project.template_name && <span>Template · {project.template_name}</span>}
              </div>
            </div>
            <div className="text-right min-w-[180px]">
              <div className="text-[10px] uppercase tracking-wider font-mono text-blue-300">Progress</div>
              <div className="font-heading text-3xl font-bold mt-1" data-testid="project-progress-pct">{project.progress_pct}%</div>
              <div className="text-xs text-slate-300 mt-0.5">{project.steps_done}/{project.steps_total} steps</div>
              <Progress value={project.progress_pct} className="h-1.5 mt-2 bg-slate-800" />
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Project details</div>
            {!headerEdit ? (
              <Button size="sm" variant="outline" onClick={() => setHeaderEdit(true)} data-testid="edit-project-header">
                <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setHeaderEdit(false); load(); }}>Cancel</Button>
                <Button size="sm" onClick={saveHeader} disabled={saving} data-testid="save-project-header" className="bg-blue-600 hover:bg-blue-700">Save</Button>
              </div>
            )}
          </div>
          {!headerEdit ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <FieldReadOnly label="Owner" value={project.owner_name} />
                <FieldReadOnly label="CSM" value={project.csm_name} />
                <FieldReadOnly label="MRR" value={fmtMoney(project.mrr)} />
                <FieldReadOnly label="Status" value={st.label} />
                <FieldReadOnly label="Start date" value={project.start_date ? fmtDate(project.start_date) : "—"} />
                <FieldReadOnly label="Target date" value={project.target_date ? fmtDate(project.target_date) : "—"} />
                <FieldReadOnly
                  label="Completed"
                  value={project.status === "completed" && project.completed_at ? fmtDate(project.completed_at) : "—"}
                  testid="project-completed-at"
                />
                <FieldReadOnly label="Steps completed" value={`${project.steps_done} / ${project.steps_total}`} />
                <FieldReadOnly label="Progress" value={`${project.progress_pct}%`} />
              </div>
              <div className="mt-4 border border-amber-200 bg-amber-50/60 rounded-md p-3" data-testid="next-action-readonly">
                <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700 flex items-center gap-1">
                  <ArrowDown className="h-3 w-3 -rotate-90" /> Next action
                </div>
                <div className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">
                  {project.next_action ? project.next_action : <span className="text-slate-400 italic">No next action set — click Edit to add one.</span>}
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Project name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-project-name" />
              </div>
              <StaffPicker label="Owner" staff={staff} value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} testid="edit-project-owner" />
              <StaffPicker label="CSM" staff={staff} value={form.csm_name} onChange={(v) => setForm({ ...form, csm_name: v })} testid="edit-project-csm" />
              <div>
                <Label className="text-xs">MRR (£)</Label>
                <Input type="number" min="0" step="0.01" value={form.mrr} onChange={(e) => setForm({ ...form, mrr: e.target.value })} placeholder="0.00" data-testid="edit-project-mrr" />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="edit-project-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="edit-project-start" />
              </div>
              <div>
                <Label className="text-xs">Target date</Label>
                <Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
              </div>
              {form.status === "completed" && (
                <div>
                  <Label className="text-xs">Completed date</Label>
                  <Input
                    type="date"
                    value={form.completed_date}
                    onChange={(e) => setForm({ ...form, completed_date: e.target.value })}
                    data-testid="edit-project-completed-date"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">Defaults to when tasks were completed — amend if the work actually finished earlier.</p>
                </div>
              )}
              <div className="md:col-span-2 lg:col-span-3">
                <Label className="text-xs">Description</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <Label className="text-xs">Next action</Label>
                <Textarea
                  rows={2}
                  value={form.next_action}
                  onChange={(e) => setForm({ ...form, next_action: e.target.value })}
                  placeholder="What is the immediate next step? e.g. 'Chase customer for admin credentials.'"
                  data-testid="edit-project-next-action"
                />
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Tasks toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="font-heading text-lg font-semibold text-slate-900">Tasks</div>
          {selectedIds.size > 0 ? (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-md px-2.5 py-1.5" data-testid="bulk-select-toolbar">
              <span className="text-xs font-medium text-blue-900">{selectedIds.size} selected</span>
              <Button size="sm" variant="ghost" onClick={clearSelection} className="h-7 px-2 text-xs">
                <X className="h-3 w-3 mr-1" /> Clear
              </Button>
              <div className="w-px h-4 bg-blue-200" />
              <Button size="sm" variant="ghost" onClick={() => setBulkStatus("completed")} data-testid="bulk-set-completed" className="h-7 px-2 text-xs text-emerald-700 hover:bg-emerald-100">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Mark completed
              </Button>
              <Button size="sm" variant="ghost" onClick={() => applyBulk("not_required")} data-testid="bulk-set-not-required" className="h-7 px-2 text-xs text-slate-700 hover:bg-slate-100">
                <MinusCircle className="h-3 w-3 mr-1" /> Not required
              </Button>
              <Button size="sm" variant="ghost" onClick={() => applyBulk("open")} data-testid="bulk-set-open" className="h-7 px-2 text-xs">
                <Circle className="h-3 w-3 mr-1" /> Re-open
              </Button>
            </div>
          ) : (
            <div className="text-xs text-slate-500">
              {localSteps.length} tasks · Tick tasks to bulk-edit
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!reordering ? (
            <>
              <Button size="sm" variant="outline" onClick={selectAll} disabled={localSteps.length === 0} data-testid="select-all">
                Select all
              </Button>
              <Button size="sm" variant="outline" onClick={() => setReordering(true)} disabled={localSteps.length < 2} data-testid="reorder-mode">
                Reorder
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove}>
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete project
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={cancelReorder}>Cancel</Button>
              <Button size="sm" onClick={saveReorder} disabled={saving} data-testid="save-reorder" className="bg-blue-600 hover:bg-blue-700">
                Save order
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Task list — bucketed by group name in first-appearance order */}
      <Card className="border-slate-200">
        <CardContent className="p-3 space-y-3" data-testid="project-checklist">
          {stepsToShow.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6">No tasks in this project.</div>
          ) : (
            (() => {
              // Bucket by group in the order groups first appear. Steps with a
              // null/empty group land in the sentinel "__UNGROUPED__" bucket which
              // renders as "General".
              const seen = new Map(); // groupKey -> [{step, absoluteIndex}]
              stepsToShow.forEach((s, absoluteIndex) => {
                const key = (s.group && s.group.trim()) || "__UNGROUPED__";
                if (!seen.has(key)) seen.set(key, []);
                seen.get(key).push({ step: s, absoluteIndex });
              });
              const groups = Array.from(seen.entries());
              return groups.map(([key, items]) => {
                const label = key === "__UNGROUPED__" ? "General" : key;
                const groupDone = items.filter(({ step }) => step.status === "completed" || step.status === "not_required" || step.completed).length;
                const isNamed = key !== "__UNGROUPED__";
                return (
                  <div key={key} className="space-y-1" data-testid={`task-group-${key}`}>
                    <div className="flex items-center justify-between gap-2 pl-1 pr-2 py-1.5 border-b border-slate-200/80">
                      <div className="flex items-center gap-2 min-w-0">
                        <Layers className={`h-3.5 w-3.5 shrink-0 ${isNamed ? "text-blue-600" : "text-slate-400"}`} />
                        <span className={`text-xs font-heading font-semibold uppercase tracking-wider truncate ${isNamed ? "text-slate-800" : "text-slate-500"}`}>
                          {label}
                        </span>
                        <span className="text-[10px] font-mono text-slate-400">
                          {groupDone}/{items.length}
                        </span>
                      </div>
                      {isNamed && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-1.5 text-[11px] text-slate-500 hover:text-blue-700"
                          onClick={() => renameGroup(key)}
                          data-testid={`rename-group-${key}`}
                          title="Rename group"
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {items.map(({ step, absoluteIndex }) => (
                        <TaskRow
                          key={step.id}
                          step={step}
                          index={absoluteIndex}
                          total={stepsToShow.length}
                          selected={selectedIds.has(step.id)}
                          onToggleSelect={() => toggleSelect(step.id)}
                          onAskComplete={() => askComplete(step)}
                          onMarkOpen={() => markOpen(step.id)}
                          onMarkNotRequired={() => markNotRequired(step.id)}
                          onEditNotes={() => askEditNotes(step)}
                          reordering={reordering}
                          onMoveUp={() => move(absoluteIndex, -1)}
                          onMoveDown={() => move(absoluteIndex, +1)}
                        />
                      ))}
                    </div>
                  </div>
                );
              });
            })()
          )}
        </CardContent>
      </Card>

      {/* Complete-task dialog: capture time + notes */}
      <CompleteTaskDialog
        open={!!completeDialog}
        stepName={completeDialog?.name}
        estimatedMinutes={completeDialog?.estMinutes}
        onCancel={() => setCompleteDialog(null)}
        onConfirm={confirmComplete}
      />

      {/* Notes-only dialog: works for tasks in any status */}
      <NotesDialog
        open={!!notesDialog}
        stepName={notesDialog?.name}
        currentNotes={notesDialog?.current}
        onCancel={() => setNotesDialog(null)}
        onConfirm={confirmNotes}
      />

      {/* Bulk-complete dialog: capture time + notes for many at once */}
      <CompleteTaskDialog
        open={bulkStatus === "completed"}
        stepName={`${selectedIds.size} selected task(s)`}
        estimatedMinutes={0}
        onCancel={() => setBulkStatus(null)}
        onConfirm={({ time, notes, completedDate }) => applyBulk("completed", {
          time_spent_minutes: Number(time) || 0,
          notes: notes || null,
          completed_at: dateToCompletedAt(completedDate),
        })}
      />
    </div>
  );
}

/* ---------------- Small helpers ---------------- */
function FieldReadOnly({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function StaffPicker({ label, staff, value, onChange, testid }) {
  const options = ["", ...Array.from(new Set((staff || []).map((u) => u.name).filter(Boolean)))];
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Unassigned" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— Unassigned —</SelectItem>
          {options.filter(Boolean).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function TaskRow({ step, index, total, selected, onToggleSelect, onAskComplete, onMarkOpen, onMarkNotRequired, onEditNotes, reordering, onMoveUp, onMoveDown }) {
  const stStatus = STEP_STATUS_MAP[step.status || (step.completed ? "completed" : "open")];
  const StatusIcon = stStatus.icon;
  const isDoneVisual = step.status === "completed";
  const isNR = step.status === "not_required";
  const hasNotes = !!(step.notes && step.notes.trim());
  return (
    <div
      data-testid={`project-step-${step.id}`}
      className={`grid grid-cols-[28px_28px_1fr_150px_auto] gap-2 items-center p-2.5 rounded-md border transition-colors ${
        isDoneVisual ? "bg-emerald-50/40 border-emerald-100" :
        isNR ? "bg-slate-50/60 border-slate-200" :
        "bg-white border-slate-100 hover:border-blue-200"
      }`}
    >
      {/* Multi-select */}
      <div className="flex items-center justify-center">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggleSelect}
          data-testid={`select-step-${step.id}`}
        />
      </div>
      {/* Order + icon */}
      <div className="text-center">
        {reordering ? (
          <div className="flex flex-col gap-0.5 items-center">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={index === 0}
              data-testid={`move-up-${step.id}`}
              className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-slate-400"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={index === total - 1}
              data-testid={`move-down-${step.id}`}
              className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-slate-400"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <StatusIcon className={`h-4 w-4 mx-auto ${stStatus.dotColor}`} />
        )}
      </div>
      {/* Body */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono text-slate-400">#{index + 1}</span>
          <div className={`font-medium ${isDoneVisual ? "text-slate-500 line-through" : isNR ? "text-slate-500 italic" : "text-slate-900"}`}>
            {step.name}
          </div>
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase border ${stStatus.color}`}>
            {stStatus.label}
          </span>
        </div>
        {step.description && (
          <div className={`text-xs mt-0.5 ${isDoneVisual || isNR ? "text-slate-400" : "text-slate-500"}`}>{step.description}</div>
        )}
        <div className="mt-1 flex items-center gap-3 text-[11px] font-mono text-slate-400 flex-wrap">
          {step.estimated_minutes > 0 && (
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Est · {fmtMinutes(step.estimated_minutes)}</span>
          )}
          {step.completed_at && step.status === "completed" && <span>Done · {fmtDate(step.completed_at)}{step.completed_by ? ` · ${step.completed_by}` : ""}</span>}
          {step.time_spent_minutes > 0 && <span>Time · {fmtMinutes(step.time_spent_minutes)}</span>}
        </div>
        {hasNotes && (
          <div className="mt-1 text-xs italic text-slate-600 bg-white/60 border border-slate-100 rounded px-2 py-1">“{step.notes}”</div>
        )}
      </div>
      {/* Status quick-select */}
      <div>
        <Select
          value={step.status || (step.completed ? "completed" : "open")}
          onValueChange={(v) => {
            if (v === "completed" && step.status !== "completed") onAskComplete();
            else if (v === "not_required") onMarkNotRequired();
            else onMarkOpen();
          }}
        >
          <SelectTrigger className="h-8 text-xs" data-testid={`step-status-${step.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {STEP_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {/* Row actions */}
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          onClick={onEditNotes}
          data-testid={`notes-btn-${step.id}`}
          className={`h-8 text-xs ${hasNotes ? "text-blue-700 border-blue-200 bg-blue-50 hover:bg-blue-100" : ""}`}
          title={hasNotes ? "Edit notes" : "Add notes"}
        >
          <MessageSquare className="h-3 w-3" />
        </Button>
        {step.status !== "completed" && (
          <Button size="sm" variant="outline" onClick={onAskComplete} data-testid={`complete-btn-${step.id}`} className="h-8 text-xs">
            <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600" /> Complete
          </Button>
        )}
      </div>
    </div>
  );
}

function NotesDialog({ open, stepName, currentNotes, onCancel, onConfirm }) {
  const [notes, setNotes] = useState("");
  useEffect(() => { if (open) setNotes(currentNotes || ""); }, [open, currentNotes]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="notes-dialog">
        <DialogHeader>
          <DialogTitle>{currentNotes ? "Edit notes" : "Add notes"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {stepName && <div className="text-sm text-slate-500">Task: <span className="text-slate-900 font-medium">{stepName}</span></div>}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Progress so far, blockers, follow-ups… kept for reporting."
              data-testid="notes-input"
              autoFocus
            />
            <div className="text-[11px] text-slate-400 mt-1">Notes are saved on the task regardless of its status.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onConfirm(notes.trim())} className="bg-blue-600 hover:bg-blue-700" data-testid="save-notes">
            Save notes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteTaskDialog({ open, stepName, estimatedMinutes, onCancel, onConfirm }) {
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [completedDate, setCompletedDate] = useState("");
  // Pre-fill the time field with the template's estimated minutes so the engineer
  // only has to override it when their actual time differs. Completed-date defaults
  // to TODAY but is editable so past work can be back-dated.
  useEffect(() => {
    if (open) {
      setTime(estimatedMinutes && Number(estimatedMinutes) > 0 ? String(estimatedMinutes) : "");
      setNotes("");
      setCompletedDate(new Date().toISOString().slice(0, 10));
    }
  }, [open, estimatedMinutes]);
  const todayIso = new Date().toISOString().slice(0, 10);
  const isBackDated = completedDate && completedDate !== todayIso;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="complete-task-dialog">
        <DialogHeader>
          <DialogTitle>Mark task complete</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {stepName && <div className="text-sm text-slate-500">Task: <span className="text-slate-900 font-medium">{stepName}</span></div>}
          <div>
            <Label className="text-xs">Completed on</Label>
            <Input
              type="date"
              value={completedDate}
              max={todayIso}
              onChange={(e) => setCompletedDate(e.target.value)}
              data-testid="complete-date-input"
            />
            <div className="text-[11px] text-slate-400 mt-1">
              {isBackDated
                ? `Back-dating to ${completedDate}. Change back to today's date if that was a mistake.`
                : "Defaults to today. Change it if the task was finished on a previous day."}
            </div>
          </div>
          <div>
            <Label className="text-xs">Time spent (minutes)</Label>
            <Input type="number" min="0" step="1" value={time} onChange={(e) => setTime(e.target.value)} placeholder="e.g. 30" data-testid="complete-time-input" />
            <div className="text-[11px] text-slate-400 mt-1">
              {estimatedMinutes > 0
                ? `Pre-filled from the template estimate (${fmtMinutes(estimatedMinutes)}). Edit if the actual time differed.`
                : "Used for capacity tracking. Leave blank for zero."}
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was done, any follow-ups…" data-testid="complete-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onConfirm({ time, notes, completedDate })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-complete">
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- New Project dialog ---------------- */
function NewProjectDialog({ accounts, templates, staff, onClose, onCreated }) {
  const [form, setForm] = useState({
    account_id: "",
    name: "",
    description: "",
    status: "planning",
    start_date: "",
    target_date: "",
    owner_name: "",
    csm_name: "",
    mrr: "",
  });
  const [steps, setSteps] = useState([]);
  const [picks, setPicks] = useState([]); // [{id, name}] templates added
  const [addSel, setAddSel] = useState("none");

  const normName = (n) => (n || "").trim().toLowerCase();

  // indices of steps whose (normalised) name is a duplicate of an earlier one
  const dupInfo = useMemo(() => {
    const seen = new Map();
    const dupIdx = new Set();
    let count = 0;
    steps.forEach((s, i) => {
      const key = normName(s.name);
      if (!key) return;
      if (seen.has(key)) { dupIdx.add(i); count++; }
      else seen.set(key, i);
    });
    return { dupIdx, count };
  }, [steps]);

  const addTemplate = (tid) => {
    setAddSel("none");
    if (!tid || tid === "none") return;
    if (picks.some((p) => p.id === tid)) { toast.info("Template already added"); return; }
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    const incoming = (t.steps || []).map((s, i) => ({
      name: s.name,
      description: s.description || "",
      assigned_to: s.assigned_to || null,
      group: s.group || t.name,
      estimated_minutes: s.estimated_minutes || 0,
      status: "open",
      __from: t.id,
    }));
    setSteps((prev) => [...prev, ...incoming].map((s, i) => ({ ...s, order: i })));
    setPicks((prev) => [...prev, { id: t.id, name: t.name }]);
    if (!form.name) setForm((f) => ({ ...f, name: t.name }));
  };

  const removeTemplate = (tid) => {
    setPicks((prev) => prev.filter((p) => p.id !== tid));
    setSteps((prev) => prev.filter((s) => s.__from !== tid).map((s, i) => ({ ...s, order: i })));
  };

  const addStep = () => setSteps([...steps, { name: "", description: "", group: "", order: steps.length, status: "open", estimated_minutes: 0 }]);
  const updStep = (i, k, v) => {
    const next = [...steps];
    next[i] = { ...next[i], [k]: v };
    setSteps(next);
  };
  const rmStep = (i) => {
    const next = [...steps];
    next.splice(i, 1);
    setSteps(next.map((s, idx) => ({ ...s, order: idx })));
  };
  const moveStep = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    setSteps(next.map((s, idx) => ({ ...s, order: idx })));
  };
  const removeDuplicates = () => {
    const seen = new Set();
    const kept = [];
    steps.forEach((s) => {
      const key = normName(s.name);
      if (key && seen.has(key)) return; // drop later duplicate
      if (key) seen.add(key);
      kept.push(s);
    });
    const removed = steps.length - kept.length;
    setSteps(kept.map((s, i) => ({ ...s, order: i })));
    toast.success(removed ? `Removed ${removed} duplicate task${removed === 1 ? "" : "s"}` : "No duplicates found");
  };

  const save = async () => {
    if (!form.account_id) return toast.error("Select an account");
    if (!form.name.trim()) return toast.error("Project name required");
    const cleanSteps = steps
      .filter((s) => s.name?.trim())
      .map((s, i) => ({
        name: s.name.trim(),
        description: s.description || null,
        assigned_to: s.assigned_to || null,
        group: (s.group && s.group.trim()) ? s.group.trim() : null,
        estimated_minutes: Number(s.estimated_minutes) || 0,
        status: "open",
        order: i,
      }));
    const mrrNum = form.mrr === "" ? null : Number(form.mrr);
    try {
      const r = await api.post("/projects", {
        account_id: form.account_id,
        name: form.name,
        description: form.description || null,
        status: form.status,
        template_ids: picks.length ? picks.map((p) => p.id) : null,
        start_date: form.start_date || null,
        target_date: form.target_date || null,
        owner_name: form.owner_name || null,
        csm_name: form.csm_name || null,
        mrr: Number.isFinite(mrrNum) ? mrrNum : null,
        steps: cleanSteps.length ? cleanSteps : null,
      });
      toast.success("Project created");
      onCreated(r.data);
    } catch (e) {
      toast.error("Create failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const totalEst = steps.reduce((a, s) => a + (Number(s.estimated_minutes) || 0), 0);

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>New project</DialogTitle>
        <DialogDescription>Add one or more product templates, review the combined tasks, remove duplicates and reorder before creating.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Account *</Label>
            <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
              <SelectTrigger data-testid="project-form-account"><SelectValue placeholder="Select account…" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Add product templates</Label>
            <Select value={addSel} onValueChange={addTemplate}>
              <SelectTrigger data-testid="project-form-template"><SelectValue placeholder="Add a template…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Add a template… —</SelectItem>
                {templates.filter((t) => !picks.some((p) => p.id === t.id)).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} ({(t.steps || []).length} steps)</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {picks.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="project-template-chips">
                {picks.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-blue-100 border border-blue-200 px-2 py-0.5 text-[11px] text-blue-800" data-testid={`template-chip-${p.id}`}>
                    <Layers className="h-3 w-3" /> {p.name}
                    <button onClick={() => removeTemplate(p.id)} className="ml-0.5 hover:text-blue-950" title="Remove template" data-testid={`remove-template-${p.id}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <Label>Project name *</Label>
          <Input data-testid="project-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Endpoint Protection rollout" />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Start date</Label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <Label>Target date</Label>
            <Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
          </div>
          <div>
            <Label>MRR (£)</Label>
            <Input type="number" min="0" step="0.01" value={form.mrr} onChange={(e) => setForm({ ...form, mrr: e.target.value })} placeholder="0.00" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <StaffPicker label="Project owner" staff={staff} value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} testid="new-project-owner" />
          <StaffPicker label="CSM" staff={staff} value={form.csm_name} onChange={(v) => setForm({ ...form, csm_name: v })} testid="new-project-csm" />
        </div>

        <div className="border-t border-slate-200 pt-4">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div>
              <div className="text-sm font-heading font-semibold text-slate-900">Project preview — {steps.length} task{steps.length === 1 ? "" : "s"}</div>
              <div className="text-xs text-slate-500">
                {picks.length > 1
                  ? "Combined from multiple templates. Review below — delete duplicates and reorder before creating."
                  : "Review the tasks — add, delete or reorder before creating."}
                {totalEst > 0 && <span className="ml-1 text-slate-400">· {Math.round(totalEst / 60 * 10) / 10}h estimated</span>}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={addStep} data-testid="add-project-step"><Plus className="h-3.5 w-3.5 mr-1" /> Add task</Button>
          </div>

          {dupInfo.count > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 mb-2" data-testid="duplicate-banner">
              <div className="flex items-center gap-2 text-xs text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                {dupInfo.count} duplicate task{dupInfo.count === 1 ? "" : "s"} detected (same name appears more than once).
              </div>
              <Button size="sm" variant="outline" className="border-amber-400 text-amber-900 hover:bg-amber-100" onClick={removeDuplicates} data-testid="remove-duplicates-btn">
                Remove {dupInfo.count} duplicate{dupInfo.count === 1 ? "" : "s"}
              </Button>
            </div>
          )}

          <div className="space-y-2">
            {steps.map((s, i) => {
              const isDup = dupInfo.dupIdx.has(i);
              return (
                <div
                  key={i}
                  data-testid={`preview-step-${i}`}
                  className={`grid grid-cols-12 gap-2 items-center rounded-md border p-2 ${isDup ? "bg-amber-50 border-amber-300" : "bg-slate-50 border-slate-100"}`}
                >
                  <div className="col-span-1 flex flex-col items-center">
                    <button onClick={() => moveStep(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up" data-testid={`move-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                    <span className="text-[11px] font-mono text-slate-400">{i + 1}</span>
                    <button onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down" data-testid={`move-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="col-span-4">
                    <Input placeholder="Task name *" value={s.name || ""} onChange={(e) => updStep(i, "name", e.target.value)} data-testid={`preview-step-name-${i}`} />
                    {isDup && <span className="inline-block mt-1 text-[10px] font-medium text-amber-700">Duplicate</span>}
                  </div>
                  <Input className="col-span-3" placeholder="Description" value={s.description || ""} onChange={(e) => updStep(i, "description", e.target.value)} />
                  <Input className="col-span-2" placeholder="Group / product" value={s.group || ""} onChange={(e) => updStep(i, "group", e.target.value)} title="Group name" />
                  <Input className="col-span-1" type="number" min="0" step="5" placeholder="Est" value={s.estimated_minutes || ""} onChange={(e) => updStep(i, "estimated_minutes", e.target.value)} title="Estimated minutes" />
                  <Button size="icon" variant="ghost" className="col-span-1" onClick={() => rmStep(i)} data-testid={`preview-step-delete-${i}`}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                  </Button>
                </div>
              );
            })}
            {steps.length === 0 && <div className="text-xs text-slate-400 italic px-2 py-1">No tasks yet — add a product template above or add tasks manually.</div>}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button data-testid="project-form-submit" onClick={save} className="bg-blue-600 hover:bg-blue-700">Create project</Button>
      </DialogFooter>
    </DialogContent>
  );
}

/* ---------------- Projects Configuration (RAG thresholds) ---------------- */
function ProjectsConfig() {
  const [cfg, setCfg] = useState(DEFAULT_RAG_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/projects/rag-config");
      setCfg({ ...DEFAULT_RAG_CONFIG, ...r.data });
      setDirty(false);
    } catch (e) {
      toast.error("Failed to load RAG config");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upd = (k, v) => {
    setCfg((c) => ({ ...c, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    // Client-side sanity checks match the backend so the user gets an immediate,
    // specific error rather than a generic 400.
    if (Number(cfg.age_amber_days) > Number(cfg.age_red_days)) {
      return toast.error("Age Amber threshold must be ≤ Red threshold.");
    }
    if (Number(cfg.inactivity_amber_days) > Number(cfg.inactivity_red_days)) {
      return toast.error("Inactivity Amber threshold must be ≤ Red threshold.");
    }
    setSaving(true);
    try {
      const payload = {
        age_amber_days: parseInt(cfg.age_amber_days, 10),
        age_red_days: parseInt(cfg.age_red_days, 10),
        inactivity_amber_days: parseInt(cfg.inactivity_amber_days, 10),
        inactivity_red_days: parseInt(cfg.inactivity_red_days, 10),
        enabled: !!cfg.enabled,
      };
      const r = await api.put("/projects/rag-config", payload);
      setCfg({ ...DEFAULT_RAG_CONFIG, ...r.data });
      setDirty(false);
      toast.success("RAG configuration saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setCfg(DEFAULT_RAG_CONFIG);
    setDirty(true);
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Loading configuration…</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="projects-config-panel">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Project RAG colour rules</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Rows on the Active projects list are tinted <span className="font-semibold text-emerald-700">Green</span>,{" "}
            <span className="font-semibold text-amber-700">Amber</span> or{" "}
            <span className="font-semibold text-red-700">Red</span> based on the worse of these two metrics.
            Completed &amp; cancelled projects are always shown neutral.
          </p>
        </CardHeader>
        <CardContent className="p-5 space-y-6">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!cfg.enabled}
              onChange={(e) => upd("enabled", e.target.checked)}
              data-testid="rag-enabled-toggle"
              className="h-4 w-4 accent-blue-600"
            />
            <span className="font-medium">Enable RAG colour-coding on the projects list</span>
          </label>

          {/* Age thresholds */}
          <div className="rounded-lg border border-slate-200 p-4 space-y-3">
            <div>
              <div className="text-sm font-heading font-semibold text-slate-900">Project age (days since start)</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Measured from <span className="font-mono">start_date</span> (falls back to created date).
                Anything above the Red threshold turns the row red.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Amber threshold (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={cfg.age_amber_days}
                  onChange={(e) => upd("age_amber_days", e.target.value)}
                  data-testid="rag-age-amber"
                />
                <div className="text-[11px] text-amber-700 mt-1">&gt; {cfg.age_amber_days || 0}d → Amber</div>
              </div>
              <div>
                <Label className="text-xs">Red threshold (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={cfg.age_red_days}
                  onChange={(e) => upd("age_red_days", e.target.value)}
                  data-testid="rag-age-red"
                />
                <div className="text-[11px] text-red-700 mt-1">&gt; {cfg.age_red_days || 0}d → Red</div>
              </div>
            </div>
          </div>

          {/* Inactivity thresholds */}
          <div className="rounded-lg border border-slate-200 p-4 space-y-3">
            <div>
              <div className="text-sm font-heading font-semibold text-slate-900">Inactivity (days since last task completed)</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Uses the most recent step <span className="font-mono">completed_at</span> on the project.
                If no task has been completed yet we fall back to the project&apos;s age.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Amber threshold (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={cfg.inactivity_amber_days}
                  onChange={(e) => upd("inactivity_amber_days", e.target.value)}
                  data-testid="rag-inactivity-amber"
                />
                <div className="text-[11px] text-amber-700 mt-1">&gt; {cfg.inactivity_amber_days || 0}d → Amber</div>
              </div>
              <div>
                <Label className="text-xs">Red threshold (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={cfg.inactivity_red_days}
                  onChange={(e) => upd("inactivity_red_days", e.target.value)}
                  data-testid="rag-inactivity-red"
                />
                <div className="text-[11px] text-red-700 mt-1">&gt; {cfg.inactivity_red_days || 0}d → Red</div>
              </div>
            </div>
          </div>

          {/* Preview swatches */}
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="text-sm font-heading font-semibold text-slate-900 mb-3">Preview</div>
            <div className="space-y-1.5">
              {[
                { level: "green", label: "On track — under both amber thresholds" },
                { level: "amber", label: `Warning — over ${cfg.age_amber_days}d old OR ${cfg.inactivity_amber_days}d inactive` },
                { level: "red", label: `Critical — over ${cfg.age_red_days}d old OR ${cfg.inactivity_red_days}d inactive` },
                { level: "neutral", label: "Completed or cancelled — no RAG applied" },
              ].map((r) => {
                const s = RAG_STYLES[r.level];
                return (
                  <div
                    key={r.level}
                    className={`flex items-center gap-3 px-3 py-2 rounded border border-slate-200 ${s.row}`}
                  >
                    <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                    <span className={`text-xs uppercase tracking-wider font-mono ${s.label}`}>{r.level}</span>
                    <span className="text-sm text-slate-700">{r.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <Button variant="ghost" onClick={resetDefaults} data-testid="rag-reset-defaults" className="text-slate-600">
              Reset to defaults
            </Button>
            <div className="flex items-center gap-2">
              {dirty && <span className="text-[11px] font-mono text-amber-700 uppercase tracking-wider">Unsaved changes</span>}
              <Button onClick={save} disabled={saving || !dirty} data-testid="rag-save" className="bg-blue-600 hover:bg-blue-700">
                {saving ? "Saving…" : "Save configuration"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------- Templates list (unchanged behaviour) ---------------- */
function TemplatesList() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/project-templates");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Archive this template?")) return;
    await api.delete(`/project-templates/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-slate-500">{items.length} templates</span>
        <Button data-testid="new-template-btn" onClick={() => setEditing({ name: "", description: "", steps: [] })} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4" /> New Template
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">No templates yet</div>
          <div className="text-sm text-slate-500 mt-1">
            Build a reusable install playbook to spin up projects faster.
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((t) => (
            <Card key={t.id} className="border-slate-200 hover:border-blue-200 transition-colors" data-testid={`template-card-${t.id}`}>
              <CardHeader className="border-b border-slate-100 flex flex-row items-start justify-between gap-3">
                <div className="flex-1">
                  <CardTitle className="text-base font-heading">{t.name}</CardTitle>
                  {t.description && <div className="text-xs text-slate-500 mt-1">{t.description}</div>}
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(t)}
                    data-testid={`edit-template-${t.id}`}
                    className="gap-1.5 text-slate-700 hover:text-blue-700 hover:border-blue-300"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => remove(t.id)}
                    data-testid={`archive-template-${t.id}`}
                    title="Archive template"
                  >
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {(() => {
                  const totalEst = (t.steps || []).reduce((n, s) => n + (Number(s.estimated_minutes) || 0), 0);
                  return (
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-3">
                      <span>{(t.steps || []).length} tasks</span>
                      {totalEst > 0 && <span className="text-slate-400">· Total est. {fmtMinutes(totalEst)}</span>}
                    </div>
                  );
                })()}
                <ol className="space-y-1 text-sm">
                  {(t.steps || []).slice(0, 6).map((s, i) => (
                    <li key={s.id || i} className="flex items-start gap-2">
                      <span className="text-[10px] font-mono text-slate-400 mt-0.5">{i + 1}.</span>
                      <span className="text-slate-700 flex-1">{s.name}</span>
                      {s.estimated_minutes > 0 && (
                        <span className="text-[10px] font-mono text-slate-400">{fmtMinutes(s.estimated_minutes)}</span>
                      )}
                    </li>
                  ))}
                  {(t.steps || []).length > 6 && <li className="text-[11px] font-mono text-slate-400">+{t.steps.length - 6} more steps…</li>}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <TemplateDialog
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TemplateDialog({ template, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: template.name || "",
    description: template.description || "",
    steps: (template.steps || []).map((s) => ({ ...s, estimated_minutes: s.estimated_minutes || 0 })),
  });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = React.useRef(null);
  const isNew = !template.id;

  const addStep = () => setForm({ ...form, steps: [...form.steps, { name: "", description: "", group: "", estimated_minutes: 0, order: form.steps.length }] });
  const updStep = (i, k, v) => {
    const next = [...form.steps];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, steps: next });
  };
  const rmStep = (i) => {
    const next = [...form.steps];
    next.splice(i, 1);
    setForm({ ...form, steps: next });
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/project-templates/parse-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parsed = r.data;
      if (!parsed.steps || parsed.steps.length === 0) {
        toast.error("CSV had no valid rows. Check your header row.");
      } else {
        // Replace the current step list with the parsed rows
        setForm((f) => ({
          ...f,
          steps: parsed.steps.map((s, i) => ({ ...s, order: i, estimated_minutes: s.estimated_minutes || 0 })),
        }));
        toast.success(`Imported ${parsed.steps.length} task(s)${parsed.skipped ? ` · ${parsed.skipped} row(s) skipped (no name)` : ""}`);
      }
      setImportResult(parsed);
    } catch (err) {
      toast.error("CSV import failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name required");
    const cleanSteps = form.steps
      .filter((s) => s.name?.trim())
      .map((s, i) => ({
        name: s.name.trim(),
        description: s.description || null,
        group: (s.group && s.group.trim()) ? s.group.trim() : null,
        estimated_minutes: Number(s.estimated_minutes) || 0,
        order: i,
      }));
    const payload = { name: form.name, description: form.description || null, steps: cleanSteps };
    try {
      if (isNew) {
        await api.post("/project-templates", payload);
        toast.success("Template created");
      } else {
        await api.patch(`/project-templates/${template.id}`, payload);
        toast.success("Template updated");
      }
      onSaved();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "New" : "Edit"} project template</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name *</Label>
            <Input data-testid="template-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Endpoint Protection rollout" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-sm font-heading font-semibold text-slate-900">Task list</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Each task has a name, optional description, and estimated minutes.
                  The estimate pre-fills the &quot;time spent&quot; field when the engineer completes the task, saving keystrokes when the actual time matches.
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={downloadCsvTemplate}
                  data-testid="download-csv-template-btn"
                  className="text-xs text-blue-700 hover:bg-blue-50"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Download template CSV
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={onFilePicked}
                  data-testid="template-csv-file-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  data-testid="template-csv-upload-btn"
                  className="text-xs"
                >
                  <Upload className="h-3.5 w-3.5 mr-1.5" /> {importing ? "Importing…" : "Import from CSV"}
                </Button>
                <Button size="sm" variant="outline" onClick={addStep} data-testid="add-template-step">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add task
                </Button>
              </div>
            </div>

            {/* CSV format guide — expanded and much more explicit */}
            <div className="bg-blue-50/60 border border-blue-200 rounded-md p-3 mb-3 text-[11px] text-slate-700" data-testid="csv-format-guide">
              <div className="font-semibold text-blue-900 uppercase tracking-wider text-[10px] mb-1.5">CSV format</div>
              <div className="space-y-1">
                <div>
                  The file must start with a <span className="font-mono bg-white px-1 rounded border border-blue-100">header row</span>. Column order does not matter and header names are case-insensitive.
                </div>
                <div>
                  <span className="font-semibold">Required column:</span>
                  <span className="ml-1"><code className="font-mono bg-white px-1 rounded border border-blue-100">name</code> — the task name.</span>
                </div>
                <div>
                  <span className="font-semibold">Optional columns:</span>
                  <span className="ml-1"><code className="font-mono bg-white px-1 rounded border border-blue-100">description</code>,</span>
                  <span className="ml-1"><code className="font-mono bg-white px-1 rounded border border-blue-100">estimated_minutes</code></span>
                  <span className="ml-1 text-slate-500">(whole number of minutes),</span>
                  <span className="ml-1"><code className="font-mono bg-white px-1 rounded border border-blue-100">group</code></span>
                  <span className="ml-1 text-slate-500">(free-text bucket — tasks sharing the same value render together, e.g. one group per product)</span>
                </div>
                <div>
                  Example row: <code className="font-mono bg-white px-1 rounded border border-blue-100">Wave 1 workstation rollout,Deploy agents to first 80 endpoints,180,Falcon EDR</code>
                </div>
                <div className="text-slate-500">
                  Rows with a blank <code className="font-mono">name</code> are skipped. Blank <code className="font-mono">group</code> → the task appears under &quot;General&quot;. Uploading replaces any tasks already listed below.
                </div>
                <div className="text-slate-500 pt-1">
                  Not sure of the exact format? Click <span className="font-semibold text-blue-700">Download template CSV</span> above for a ready-to-fill file.
                </div>
              </div>
            </div>

            {importResult?.errors?.length > 0 && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3" data-testid="csv-import-errors">
                <div className="font-semibold mb-1">CSV import warnings:</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {importResult.errors.slice(0, 6).map((e, idx) => <li key={idx}>{e}</li>)}
                  {importResult.errors.length > 6 && <li>…and {importResult.errors.length - 6} more.</li>}
                </ul>
              </div>
            )}
            <div className="space-y-2">
              {form.steps.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-md border border-slate-100 p-2" data-testid={`template-step-row-${i}`}>
                  <span className="col-span-1 text-center text-[11px] font-mono text-slate-400">{i + 1}</span>
                  <Input className="col-span-4" placeholder="Task name *" value={s.name || ""} onChange={(e) => updStep(i, "name", e.target.value)} />
                  <Input className="col-span-3" placeholder="Description" value={s.description || ""} onChange={(e) => updStep(i, "description", e.target.value)} />
                  <Input
                    className="col-span-2"
                    placeholder="Group"
                    value={s.group || ""}
                    onChange={(e) => updStep(i, "group", e.target.value)}
                    data-testid={`template-step-group-${i}`}
                    title="Group name — tasks sharing this value render together (e.g. per product)"
                  />
                  <Input
                    className="col-span-1"
                    type="number" min="0" step="5"
                    placeholder="Est"
                    value={s.estimated_minutes || ""}
                    onChange={(e) => updStep(i, "estimated_minutes", e.target.value)}
                    data-testid={`template-step-estimate-${i}`}
                    title="Estimated minutes"
                  />
                  <Button size="icon" variant="ghost" className="col-span-1" onClick={() => rmStep(i)}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                  </Button>
                </div>
              ))}
              {form.steps.length === 0 && <div className="text-xs text-slate-400 italic px-2 py-1">Add tasks manually with &quot;Add task&quot;, or bulk-import from a CSV.</div>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="template-form-submit" onClick={save} className="bg-blue-600 hover:bg-blue-700">
            {isNew ? "Create template" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
